// Bullet/delta bar — the headline visual on every unexpanded dimension card.
//
// A horizontal track with a centre tick (baseline) and a filled segment
// extending right (positive delta) or left (negative delta) from that tick.
// Fill colour is green when the delta is "good for the user" given the
// dimension's direction, red otherwise. Below the bar we render the
// percentage delta as text (or "baseline" if unmodified).
//
// Why a relative-percentage scale (capped at +/- 20%) and not absolute:
//   - The six dimensions are in different units (euros, ratios, percentages).
//     A common visual axis requires normalisation.
//   - 20% is a generous M1 cap; clipped values are rare in practice and the
//     numeric label below carries the exact figure.
//   - state_budget's baseline can be near zero, so we fall back to absolute
//     scale (capped at the absolute reform value) when baseline ~= 0.

import {
  GAIN_COLOUR,
  LOSS_COLOUR,
  isGoodDelta,
  deltaTextColour,
} from "../lib/dimensions";
import type { Dimension } from "../lib/dimensions";

interface Props {
  dimension: Dimension;
  value: number;
  baseline: number;
  isModified: boolean;
  /** Visual size — "grid" cards are bigger, "strip" cards smaller. */
  size?: "grid" | "strip";
}

const NEUTRAL_COLOUR = "#c9c5d6";
const CAP_PERCENT = 0.2;

export function BulletDeltaBar({
  dimension,
  value,
  baseline,
  isModified,
  size = "grid",
}: Props): JSX.Element {
  const delta = value - baseline;
  const fraction = computeFraction(delta, baseline);
  const showAsBaseline = !isModified || Math.abs(fraction) < 1e-4;
  const fillColour = showAsBaseline
    ? NEUTRAL_COLOUR
    : isGoodDelta(delta, dimension.goodWhen)
      ? GAIN_COLOUR
      : LOSS_COLOUR;
  const fillWidthPct = Math.min(Math.abs(fraction), CAP_PERCENT) / CAP_PERCENT * 50;
  const fillLeftPct = fraction < 0 ? 50 - fillWidthPct : 50;
  const trackHeight = size === "strip" ? 6 : 8;

  return (
    <div className={`beamm-bullet-bar beamm-bullet-bar--${size}`}>
      <div
        className="beamm-bullet-bar-track"
        style={{ height: `${trackHeight}px` }}
        role="img"
        aria-label={ariaLabel(dimension, value, baseline, isModified)}
      >
        {!showAsBaseline && (
          <div
            className="beamm-bullet-bar-fill"
            style={{
              left: `${fillLeftPct}%`,
              width: `${fillWidthPct}%`,
              backgroundColor: fillColour,
            }}
          />
        )}
        <div className="beamm-bullet-bar-tick" />
      </div>
      {size === "grid" && (
        <div className="beamm-bullet-bar-label">
          {showAsBaseline ? (
            <span className="beamm-bullet-bar-baseline">baseline</span>
          ) : (
            <span
              className="beamm-bullet-bar-delta"
              // Text tier, not the bar's fill colour: the label is small text
              // and must clear AA body contrast (#199 loss-text).
              style={{ color: deltaTextColour(delta, dimension.goodWhen) }}
            >
              {formatDelta(delta, baseline, dimension)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function computeFraction(delta: number, baseline: number): number {
  if (Math.abs(baseline) < 1e-6) {
    // Degenerate baseline (e.g. state_budget at zero). Fall back to a
    // unit scale tied to delta itself so the bar still has something to
    // show. Visually-it's a sign indicator more than a magnitude.
    return Math.sign(delta) * Math.min(Math.abs(delta) / 1, CAP_PERCENT);
  }
  return delta / baseline;
}

function formatDelta(
  delta: number,
  baseline: number,
  dimension: Dimension,
): string {
  if (Math.abs(baseline) < 1e-6) {
    const sign = delta >= 0 ? "+" : "−";
    return `${sign}${dimension.formatter(Math.abs(delta))}`;
  }
  const pct = (delta / baseline) * 100;
  const sign = pct >= 0 ? "+" : "−";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

function ariaLabel(
  dimension: Dimension,
  value: number,
  baseline: number,
  isModified: boolean,
): string {
  if (!isModified) {
    return `${dimension.label} at baseline: ${dimension.formatter(value)}`;
  }
  const delta = value - baseline;
  return `${dimension.label}: ${dimension.formatter(value)}, delta ${formatDelta(delta, baseline, dimension)} from baseline`;
}
